import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** The two kinds of ingress the Operations page lists. */
export type IngressKindV3 = 'http' | 'mtls';

/** The Ziti identity an ingress runs as, read live. It carries no credential. */
export interface IngressIdentityV3 {
    id: string;
    name?: string;
    status?: 'PENDING' | 'ENROLLED' | 'ONLINE' | 'OFFLINE';
    disabled?: boolean;
    roleAttributes?: string[];
    /** When the outstanding one-time token expires. Set only while the identity is PENDING. */
    enrollmentExpiresAt?: string;
    sdkInfo?: Record<string, string>;
    envInfo?: Record<string, string>;
    createdAt?: string;
    updatedAt?: string;
}

/** The ziti-ingress of an account, as the listing returns it. */
export interface HttpIngressV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    networkId: string;
    status: 'NEW' | 'PENDING_DEPLOY' | 'DEPLOYED' | 'PENDING_REDEPLOY' | 'ERROR' | 'DELETING';
    zitiIdentityId?: string;
    identitySecretArn?: string;
    ipAddress?: string;
    dnsName?: string;
    dnsRecordName?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
    _links?: Record<string, { href: string }>;
}

/** One ziti-ingress with its live state. A deleted one has no live fields. */
export interface HttpIngressDetailV3 extends HttpIngressV3 {
    identities?: IngressIdentityV3[];
    /** Hostname to Ziti service name, from the identity's appData. */
    hosts?: Record<string, unknown>;
    storedIdentitySecretArn?: string;
    stack?: Record<string, any>;
    /** The live lookups that failed. */
    problems: string[];
}

/** The mTLS ingress of one TCP share. Its id is the share's id. */
export interface MtlsIngressV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    name: string;
    status: string;
    ingressPort: number;
    access?: string;
    zitiServiceId?: string;
    ingressZitiId?: string;
    hostingId?: string;
    clientCertificateId?: string;
    useIntermediateCertificate?: boolean;
    allowedSubjects?: string[];
    allowedCommonNamePrefixes?: string[];
    requiredOrganizationalUnit?: string;
    serverCertificateId?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
    _links?: Record<string, { href: string }>;
}

/** One mTLS ingress with its live state. The progress is unsanitized. */
export interface MtlsIngressDetailV3 extends MtlsIngressV3 {
    identities?: IngressIdentityV3[];
    identityParameterArn?: string;
    progress?: Record<string, any>;
    /** The live lookups that failed. */
    problems: string[];
}

export interface IngressPageV3<T> {
    ingresses: T[];
    total: number | null;
}

/** What one listing read asks for. The service counts pages from 0. */
export interface IngressQueryV3 {
    deleted?: boolean;
    statuses?: string[];
    sort?: string;
    page?: number;
    size?: number;
}

export const DEFAULT_INGRESS_PAGE_SIZE = 30;

/** The HAL collection name of each kind, and the path segment it is read from. */
const INGRESS_PATHS: Record<IngressKindV3, { path: string; collection: string }> = {
    http: { path: 'http-ingresses', collection: 'httpIngresses' },
    mtls: { path: 'mtls-ingresses', collection: 'mtlsIngresses' },
};

/**
 * Reads the HTTP and mTLS ingresses of a v3 frontdoor, and retries or deletes one. For NetFoundry support only:
 * the `ingress` resource type reaches no other role, and only NF Support (Intervention) may change one.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3IngressService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * One page of `frontdoorId`'s ingresses of `kind`, counted from 0. `deleted` lists the deleted ones
     * instead of the live ones, and `statuses` narrows to any of them.
     */
    public findIngresses(kind: 'http', frontdoorId: string, query?: IngressQueryV3): Observable<IngressPageV3<HttpIngressV3>>;
    public findIngresses(kind: 'mtls', frontdoorId: string, query?: IngressQueryV3): Observable<IngressPageV3<MtlsIngressV3>>;
    public findIngresses(kind: IngressKindV3, frontdoorId: string, query: IngressQueryV3 = {}): Observable<IngressPageV3<any>> {
        let params = new HttpParams()
            .set('sort', query.sort ?? 'createdAt,DESC')
            .set('size', String(query.size ?? DEFAULT_INGRESS_PAGE_SIZE))
            .set('page', String(query.page ?? 0));
        if (query.deleted) {
            params = params.set('deleted', 'true');
        }
        // Appended, not set, so several values stay several parameters.
        (query.statuses ?? []).forEach((status) => {
            params = params.append('status', status);
        });
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/${INGRESS_PATHS[kind].path}`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(kind, response)));
    }

    public getHttpIngress(frontdoorId: string, id: string): Observable<HttpIngressDetailV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/${INGRESS_PATHS.http.path}/${id}`, { headers: this.headers() })
            .pipe(map((response: any) => response as HttpIngressDetailV3));
    }

    public getMtlsIngress(frontdoorId: string, id: string): Observable<MtlsIngressDetailV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/${INGRESS_PATHS.mtls.path}/${id}`, { headers: this.headers() })
            .pipe(map((response: any) => response as MtlsIngressDetailV3));
    }

    /** Starts the build of a ziti-ingress for an account with no live one. The service answers 409 when it has one. */
    public createHttpIngress(frontdoorId: string): Observable<HttpIngressV3> {
        const url = `${this.apiUrl}/v3/${frontdoorId}/${INGRESS_PATHS.http.path}`;
        return this.http.post<HttpIngressV3>(url, {}, { headers: this.headers() });
    }

    /** Starts a new build of a failed ingress. The service answers 409 unless the ingress failed. */
    public retryIngress(kind: IngressKindV3, frontdoorId: string, id: string): Observable<HttpIngressV3 | MtlsIngressV3> {
        return this.http.post<HttpIngressV3 | MtlsIngressV3>(`${this.ingressUrl(kind, frontdoorId, id)}/retry`, {}, { headers: this.headers() });
    }

    /** Rolls a deployed ziti-ingress onto new tasks. The service answers 409 unless the ingress is DEPLOYED. */
    public redeployHttpIngress(frontdoorId: string, id: string): Observable<HttpIngressV3> {
        return this.http.post<HttpIngressV3>(`${this.ingressUrl('http', frontdoorId, id)}/redeploy`, {}, { headers: this.headers() });
    }

    /** Starts the teardown of an ingress. An mTLS ingress keeps its share, which ends in ERROR. */
    public deleteIngress(kind: IngressKindV3, frontdoorId: string, id: string): Observable<void> {
        return this.http.delete<void>(this.ingressUrl(kind, frontdoorId, id), { headers: this.headers() });
    }

    /* Internals -------------------------------------------------------------- */

    private ingressUrl(kind: IngressKindV3, frontdoorId: string, id: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/${INGRESS_PATHS[kind].path}/${id}`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    private readPage(kind: IngressKindV3, response: any): IngressPageV3<any> {
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        const embedded = response?._embedded;
        let ingresses: any[] = [];
        if (embedded) {
            const collection = embedded[INGRESS_PATHS[kind].collection] ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                ingresses = collection;
            } else {
                this.logger.error('findIngresses: unrecognised response shape', response);
            }
        }
        // An empty page carries no _embedded at all, which is not an error.
        return { ingresses, total };
    }
}
